import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, User, Lock, Mail, X, ArrowLeft, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from './service/authService';
import toast, { Toaster } from 'react-hot-toast';

export default function Login() {
  const [activeTab, setActiveTab] = useState('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [otpPurpose, setOtpPurpose] = useState('');
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [tempNewPassword, setTempNewPassword] = useState(''); // Store new password for forgot password flow
  const otpInputs = useRef([]);
  const navigate = useNavigate();

  const [signUpData, setSignUpData] = useState({
    name: '',
    contact: '',
    password: '',
    confirmPassword: '',
  });

  const [signInData, setSignInData] = useState({
    contact: '',
    password: '',
  });

  const [forgotPasswordData, setForgotPasswordData] = useState({
    contact: '',
    newPassword: '',
  });

  // Auto-focus first OTP input when modal opens
  useEffect(() => {
    if (showOtpModal && otpInputs.current[0]) {
      otpInputs.current[0].focus();
    }
  }, [showOtpModal]);

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    setSignUpData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSignInChange = (e) => {
    const { name, value } = e.target;
    setSignInData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOtpChange = (e, index) => {
    const { value } = e.target;

    if (value.match(/^\d$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < 5) {
        otpInputs.current[index + 1].focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        otpInputs.current[index - 1].focus();
      }
    }
  };

  // Helper function to validate email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to split full name into first and last name
  const splitName = (fullName) => {
    const nameParts = fullName.trim().split(' ');
    
    if (nameParts.length === 1) {
      return {
        firstName: nameParts[0],
        lastName: nameParts[0]
      };
    }
    
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
    
    return { firstName, lastName };
  };

  // 1. SIGN UP HANDLER
  const handleSignUpSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!signUpData.name.trim()) {
      toast.error('Full name is required');
      return;
    }

    if (signUpData.name.trim().length < 2) {
      toast.error('Please enter a valid name');
      return;
    }

    if (!signUpData.contact.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!isValidEmail(signUpData.contact)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (signUpData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (signUpData.password !== signUpData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    const { firstName, lastName } = splitName(signUpData.name);
    setLoading(true);

    try {
      const result = await authService.signUp(
        signUpData.contact,
        signUpData.password,
        firstName,
        lastName
      );

      if (result.success && result.data.success) {
        toast.success('Account created! Check your email for verification code.');
        setTempEmail(signUpData.contact);
        setOtpPurpose('signup');
        setShowOtpModal(true);
      } else {
        toast.error(result.message || 'Sign up failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Signup exception:', error);
    } finally {
      setLoading(false);
    }
  };

  // 2. SIGN IN HANDLER
  const handleSignInSubmit = async (e) => {
    e.preventDefault();

    if (!signInData.contact.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!isValidEmail(signInData.contact)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!signInData.password) {
      toast.error('Password is required');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.login(
        signInData.contact,
        signInData.password
      );

      if (result.success && result.data.success) {
        toast.success('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1000);
      } else {
        // Handle specific error cases
        if (result.message && result.message.includes('verify your email')) {
          toast.error(result.message);
          setTempEmail(signInData.contact);
          setOtpPurpose('signup');
          setShowOtpModal(true);
        } else {
          toast.error(result.message || 'Login failed');
        }
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Login exception:', error);
    } finally {
      setLoading(false);
    }
  };

  // 3. FORGOT PASSWORD HANDLER
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!forgotPasswordData.contact.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!isValidEmail(forgotPasswordData.contact)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (forgotPasswordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await authService.forgotPassword(forgotPasswordData.contact);

      if (result.success && result.data.success) {
        toast.success('Reset code sent to your email!');
        setTempEmail(forgotPasswordData.contact);
        setTempNewPassword(forgotPasswordData.newPassword);
        setOtpPurpose('forgot-password');
        setShowOtpModal(true);
      } else {
        toast.error(result.message || 'Failed to send reset code');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Forgot password exception:', error);
    } finally {
      setLoading(false);
    }
  };

  // 4. OTP VERIFICATION HANDLER
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      toast.error('Please enter complete 6-digit code');
      return;
    }

    setLoading(true);

    try {
      if (otpPurpose === 'signup') {
        // Verify signup OTP
        const result = await authService.confirmSignUp(tempEmail, otpCode);

        if (result.success && result.data.success) {
          toast.success('Email verified successfully! You can now log in.');
          setShowOtpModal(false);
          setOtp(Array(6).fill(''));
          setActiveTab('signin');
          setSignInData({ ...signInData, contact: tempEmail });
        } else {
          toast.error(result.message || 'Verification failed');
        }
      } else if (otpPurpose === 'forgot-password') {
        // Verify forgot password OTP and reset password
        const result = await authService.confirmForgotPassword(
          tempEmail,
          tempNewPassword,
          otpCode
        );

        if (result.success && result.data.success) {
          toast.success('Password reset successful! Please log in with your new password.');
          setShowOtpModal(false);
          setOtp(Array(6).fill(''));
          setShowForgotPassword(false);
          setActiveTab('signin');
          setSignInData({ ...signInData, contact: tempEmail });
          setForgotPasswordData({ contact: '', newPassword: '' });
        } else {
          toast.error(result.message || 'Password reset failed');
        }
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('OTP verification exception:', error);
    } finally {
      setLoading(false);
    }
  };

  // 5. RESEND OTP HANDLER
  const handleResendOtp = async () => {
    if (!tempEmail) {
      toast.error('Email not found. Please try again.');
      return;
    }

    setResendLoading(true);

    try {
      const result = await authService.resendConfirmationCode(tempEmail);

      if (result.success && result.data.success) {
        toast.success('Verification code resent!');
        setOtp(Array(6).fill(''));
        if (otpInputs.current[0]) {
          otpInputs.current[0].focus();
        }
      } else {
        toast.error(result.message || 'Failed to resend code');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Resend OTP exception:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtp(Array(6).fill(''));
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordData({ contact: '', newPassword: '' });
  };

  return (
    <>
      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-background to-emerald-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Sarayu</h1>
            <p className="text-gray-600">Your AI Travel Companion</p>
          </div>

          {/* Main Card - Login/Signup */}
          {!showForgotPassword ? (
            <div className="glass rounded-2xl p-8 animate-slideUp">
              {/* Tabs */}
              <div className="flex mb-8 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('signin')}
                  disabled={loading}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                    activeTab === 'signin'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  } disabled:opacity-50`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setActiveTab('signup')}
                  disabled={loading}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                    activeTab === 'signup'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  } disabled:opacity-50`}
                >
                  Sign Up
                </button>
              </div>

              {/* Sign In Form */}
              {activeTab === 'signin' && (
                <form onSubmit={handleSignInSubmit} className="space-y-6 animate-fadeIn">
                  <div>
                    <label htmlFor="signin-contact" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="signin-contact"
                        name="contact"
                        value={signInData.contact}
                        onChange={handleSignInChange}
                        required
                        disabled={loading}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="signin-password"
                        name="password"
                        value={signInData.password}
                        onChange={handleSignInChange}
                        required
                        disabled={loading}
                        className="w-full pl-10 pr-12 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      disabled={loading}
                      className="text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>
              )}

              {/* Sign Up Form */}
              {activeTab === 'signup' && (
                <form onSubmit={handleSignUpSubmit} className="space-y-6 animate-fadeIn">
                  <div>
                    <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="signup-name"
                        name="name"
                        value={signUpData.name}
                        onChange={handleSignUpChange}
                        required
                        minLength={2}
                        disabled={loading}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="signup-contact" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="signup-contact"
                        name="contact"
                        value={signUpData.contact}
                        onChange={handleSignUpChange}
                        required
                        disabled={loading}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="signup-password"
                        name="password"
                        value={signUpData.password}
                        onChange={handleSignUpChange}
                        required
                        minLength={8}
                        disabled={loading}
                        className="w-full pl-10 pr-12 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Create a password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                  </div>

                  <div>
                    <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="signup-confirm-password"
                        name="confirmPassword"
                        value={signUpData.confirmPassword}
                        onChange={handleSignUpChange}
                        required
                        minLength={8}
                        disabled={loading}
                        className="w-full pl-10 pr-12 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={loading}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Sign Up'
                    )}
                  </button>
                </form>
              )}

              <p className="text-xs text-gray-600 text-center mt-6">
                By continuing, you agree to Sarayu's{' '}
                <Link to="/terms" className="text-indigo-600 hover:text-indigo-800">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-indigo-600 hover:text-indigo-800">
                  Privacy Policy
                </Link>
              </p>
            </div>
          ) : (
            /* Forgot Password Card */
            <div className="glass rounded-2xl p-8 animate-slideUp">
              <button
                onClick={handleBackToLogin}
                className="flex items-center text-indigo-600 hover:text-indigo-800 mb-6"
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </button>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset Password</h2>
              <p className="text-gray-600 mb-6">Enter your email and new password</p>

              <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
                <div>
                  <label htmlFor="forgot-contact" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="forgot-contact"
                      name="contact"
                      value={forgotPasswordData.contact}
                      onChange={handleForgotPasswordChange}
                      required
                      disabled={loading}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="forgot-new-password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="forgot-new-password"
                      name="newPassword"
                      value={forgotPasswordData.newPassword}
                      onChange={handleForgotPasswordChange}
                      required
                      minLength={8}
                      disabled={loading}
                      className="w-full pl-10 pr-12 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all disabled:bg-gray-100"
                      placeholder="Create new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      disabled={loading}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                </div>

                <button 
                  type="submit" 
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* OTP Verification Modal */}
        {showOtpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-slideUp">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Verification Code</h3>
                <button
                  onClick={closeOtpModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-gray-600 mb-8">
                Enter the 6-digit code sent to{' '}
                <span className="font-semibold">{tempEmail}</span>
              </p>

              <form onSubmit={handleOtpSubmit}>
                <div className="flex justify-center gap-3 mb-8">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpInputs.current[index] = el)}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(e, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      disabled={loading}
                      className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all disabled:bg-gray-100"
                    />
                  ))}
                </div>

                <button 
                  type="submit" 
                  className="w-full btn-primary mb-4 flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>

                <p className="text-center text-sm text-gray-600">
                  Didn't receive the code?{' '}
                  <button 
                    type="button" 
                    onClick={handleResendOtp}
                    className="text-indigo-600 hover:text-indigo-800 font-semibold disabled:opacity-50"
                    disabled={loading || resendLoading}
                  >
                    {resendLoading ? 'Sending...' : 'Resend'}
                  </button>
                </p>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
